import React, { useEffect, useMemo, useState } from "react";
import isEqual from "lodash/isEqual";
import { economyApi } from "../../../../api/economy";
import { fetchAccountingResultReportAccountDetail } from "../../../../api/accounting";
import { normalizeError } from "../../../../operationFeedback/normalizeError";
import { Button, Bars, ErrorBox, GROUPS, Guide, Kpis, Select, Segmented, SectionHeading, useEconomyText, useRemote } from "./shared";

const OPERATING_SECTIONS = ["cost_of_goods", "other_operating_expenses"];

function operatingRows(month) {
  return (month.accounts || []).filter((row) => OPERATING_SECTIONS.includes(row.section));
}

function monthOperatingTotal(month) {
  if (!month?.available) return null;
  return operatingRows(month).reduce((sum, row) => sum + row.amount, 0);
}

function supplierKey(row) {
  return row.name || "";
}

function percentOverAverage(last, average) {
  if (!average || average <= 0) return null;
  return Math.round((last / average - 1) * 100);
}

export function Operating({ source, onEditingChange, controls }) {
  const { t, money, monthName } = useEconomyText();
  const savedTypes = useRemote((signal) => economyApi.settings("operating", signal), []);
  const [types, setTypes] = useState(null);
  const [version, setVersion] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [observed, setObserved] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => { if (savedTypes.data && !types) { setTypes(savedTypes.data.values?.account_types || {}); setVersion(savedTypes.data.lock_version); } }, [savedTypes.data, types]);
  const blocked = saving || saveError?.outcome === "unknown" || saveError?.httpStatus === 409;
  const save = async () => {
    if (blocked) return;
    setSaving(true); setSaveError(null); setConfirmed(false);
    try { const response = await economyApi.saveSettings("operating", { values: { account_types: types }, lock_version: version }); setVersion(response.data.lock_version); setDirty(false); setConfirmed(true); onEditingChange?.(false); }
    catch (failure) { setSaveError(failure); }
    finally { setSaving(false); }
  };
  const check = async () => {
    setSaving(true);
    try { const response = await economyApi.settings("operating"); if (isEqual(response.data.values?.account_types, types)) { setVersion(response.data.lock_version); setDirty(false); setConfirmed(true); setSaveError(null); onEditingChange?.(false); } else { setObserved(response.data); setSaveError({ httpStatus: 409, outcome: "not_applied", fieldErrors: [] }); } }
    catch (failure) { setSaveError(failure); }
    finally { setSaving(false); }
  };
  const [count, setCount] = useState(3);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const months = useMemo(() => (source.rolling_months || source.months).slice(-count), [source.rolling_months, source.months, count]);
  const accounts = useMemo(() => {
    const map = new Map();
    months.forEach((month) => operatingRows(month).forEach((row) => {
      const previous = map.get(row.account_number) || { ...row, amount: 0, posting_count: 0 };
      map.set(row.account_number, { ...previous, amount: previous.amount + row.amount, posting_count: previous.posting_count + row.posting_count });
    }));
    return [...map.values()].sort((a, b) => String(a.account_number).localeCompare(String(b.account_number)));
  }, [months]);
  const suppliers = useMemo(() => {
    const map = new Map();
    months.forEach((month) => (month.suppliers || []).forEach((row) => {
      const key = supplierKey(row);
      const previous = map.get(key) || { name: row.name, amount: 0, posting_count: 0, invoice_count: 0 };
      map.set(key, {
        name: row.name,
        amount: previous.amount + row.amount,
        posting_count: previous.posting_count + (row.posting_count || 0),
        invoice_count: previous.invoice_count + (row.invoice_count || 0),
      });
    }));
    return [...map.values()].sort((a, b) => b.amount - a.amount || String(a.name).localeCompare(String(b.name)));
  }, [months]);
  const categories = useMemo(() => {
    const totals = Object.fromEntries(GROUPS.map((group) => [group, 0]));
    accounts.forEach((row) => {
      const group = totals[row.group] == null ? "other" : row.group;
      totals[group] += row.amount;
    });
    return GROUPS.map((group) => ({ group, amount: totals[group] })).filter((row) => row.amount);
  }, [accounts]);
  const insights = useMemo(() => {
    const notes = [];
    if (months.length >= 3) {
      const last = months[months.length - 1];
      (last.suppliers || []).forEach((row) => {
        const average = months.reduce((sum, month) => sum + (month.suppliers || []).filter((entry) => supplierKey(entry) === supplierKey(row)).reduce((inner, entry) => inner + entry.amount, 0), 0) / months.length;
        const percent = percentOverAverage(row.amount, average);
        if (percent != null && percent >= 35 && row.amount - average >= 1000) {
          notes.push({ key: `high-${supplierKey(row)}`, text: t("efficiencyHigh", { name: row.name, percent }) });
        }
      });
      operatingRows(last).forEach((row) => {
        const average = months.reduce((sum, month) => sum + operatingRows(month).filter((entry) => entry.account_number === row.account_number).reduce((inner, entry) => inner + entry.amount, 0), 0) / months.length;
        const percent = percentOverAverage(row.amount, average);
        if (percent != null && percent >= 35 && row.amount - average >= 1000) {
          notes.push({ key: `account-${row.account_number}`, text: t("efficiencyHighAccount", { name: `${row.account_number} · ${row.account_name}`, percent }) });
        }
      });
    }
    const unclassified = accounts.filter((row) => !types?.[row.account_number]);
    if (unclassified.length) notes.push({ key: "unclassified", text: t("efficiencyUnclassified", { count: unclassified.length }) });
    suppliers.forEach((row) => {
      const pieces = Math.max(row.invoice_count || 0, row.posting_count || 0);
      const average = pieces ? row.amount / pieces : 0;
      if (pieces >= 6 && average > 0 && average < 2000) {
        notes.push({ key: `small-${supplierKey(row)}`, text: t("efficiencySmallInvoices", { name: row.name, count: pieces }) });
      }
    });
    return notes;
  }, [accounts, months, suppliers, t, types]);
  const available = months.length === count && months.every((month) => month.available);
  const total = accounts.reduce((sum, row) => sum + row.amount, 0) / months.length;
  const open = async (account) => {
    if (pending) return;
    setPending(true); setError(null); setDetail(null);
    const from = `${months[0].year || source.year}-${String(months[0].month).padStart(2, "0")}-01`;
    const last = months[months.length - 1].month;
    const to = `${source.year}-${String(last).padStart(2, "0")}-${new Date(source.year, last, 0).getDate()}`;
    try {
      const response = await fetchAccountingResultReportAccountDetail({ accountNumber: account.account_number, year: source.year, dateFrom: from, dateTo: to });
      if (response.error) throw normalizeError({ data: response.data });
      setDetail({ account, data: response.data });
    } catch (failure) { setError(normalizeError(failure)); }
    finally { setPending(false); }
  };
  return <section className="eco-stack">
    <SectionHeading title={t("operatingTitle")} description={t("operatingIntro")} controls={controls} />
    <Segmented label={t("monthlyEstimate")} value={count} onChange={(value) => { setCount(Number(value)); setDetail(null); }} options={[[1, t("lastMonth")], [3, t("average3")], [6, t("average6")]]} />
    <Guide>{t("operatingHelp")}</Guide>
    <ErrorBox error={savedTypes.error} onRetry={savedTypes.refresh} /><ErrorBox error={saveError} />
    {dirty && <div className="eco-toolbar"><span className="eco-muted">{t("unsaved")}</span><Button className="eco-primary" onClick={save} disabled={blocked}>{t("save")}</Button></div>}
    {saveError && blocked && <Button onClick={check} disabled={saving}>{t("checkStatus")}</Button>}
    {observed && <Button onClick={() => { setTypes(observed.values?.account_types || {}); setVersion(observed.lock_version); setDirty(false); setObserved(null); setSaveError(null); onEditingChange?.(false); }}>{t("useSaved")}</Button>}
    {confirmed && <p className="eco-success" role="status">{t("saved")}</p>}
    {!available ? <p className="eco-warning">{t("syncMissing")}</p> : <>
      <Kpis highlight="monthlyEstimate" values={{ monthlyEstimate: total, fixed: accounts.filter((row) => types?.[row.account_number] === "fixed").reduce((sum, row) => sum + row.amount / months.length, 0), variable: accounts.filter((row) => types?.[row.account_number] === "variable").reduce((sum, row) => sum + row.amount / months.length, 0), annualEstimate: total * 12, postings: accounts.reduce((sum, row) => sum + row.posting_count, 0) }} />
      <div className="eco-card"><Bars rows={(source.rolling_months || source.months).slice(-6).map((month) => ({ label: `${monthName(month.month)} ${month.year || source.year}`, value: monthOperatingTotal(month) }))} /></div>
      <div className="eco-card">
        <h3>{t("categoriesTitle")}</h3>
        <p className="eco-muted">{t("categoriesHelp")}</p>
        <div className="eco-table-scroll" tabIndex={0}><table className="eco-table"><thead><tr>{["category", "monthlyEstimate", "annualEstimate"].map((key) => <th key={key}>{t(key)}</th>)}</tr></thead><tbody>{categories.map((row) => <tr key={row.group}><td>{t(row.group)}</td><td>{money(row.amount / months.length)}</td><td>{money(row.amount / months.length * 12)}</td></tr>)}</tbody></table></div>
      </div>
      <div className="eco-card">
        <h3>{t("suppliersTitle")}</h3>
        <p className="eco-muted">{t("suppliersHelp")}</p>
        {suppliers.length ? <div className="eco-table-scroll" tabIndex={0}><table className="eco-table"><thead><tr>{["supplier", "invoices", "postings", "monthlyEstimate", "annualEstimate"].map((key) => <th key={key}>{t(key)}</th>)}</tr></thead><tbody>{suppliers.map((row) => <tr key={supplierKey(row)}><td>{row.name || t("unknownSupplier")}</td><td>{row.invoice_count}</td><td>{row.posting_count}</td><td>{money(row.amount / months.length)}</td><td>{money(row.amount / months.length * 12)}</td></tr>)}</tbody></table></div> : <p className="eco-muted">{t("noSuppliers")}</p>}
      </div>
      <div className="eco-card">
        <h3>{t("efficiencyTitle")}</h3>
        <p className="eco-muted">{t("efficiencyHelp")}</p>
        {insights.length ? <ul>{insights.map((note) => <li key={note.key}>{note.text}</li>)}</ul> : <p>{t("efficiencyOk")}</p>}
      </div>
      <div className="eco-table-scroll" tabIndex={0}><table className="eco-table"><thead><tr>{["account", "method", "postings", "monthlyEstimate", "annualEstimate", "details"].map((key) => <th key={key}>{t(key)}</th>)}</tr></thead><tbody>{accounts.map((row) => <tr key={row.account_number}><td>{row.account_number} · {row.account_name}</td><td><Select label={`${t("method")} ${row.account_number}`} value={types?.[row.account_number] || ""} disabled={!types || blocked} options={[["", t("unclassified")], ["fixed", t("fixed")], ["variable", t("variable")]]} onChange={(kind) => { const next = { ...types }; if (kind) next[row.account_number] = kind; else delete next[row.account_number]; setTypes(next); setDirty(true); setConfirmed(false); onEditingChange?.(true); }} /></td><td>{row.posting_count}</td><td>{money(row.amount / months.length)}</td><td>{money(row.amount / months.length * 12)}</td><td><Button disabled={pending} onClick={() => open(row)}>{t("details")}</Button></td></tr>)}</tbody></table></div>
    </>}
    <ErrorBox error={error} />
    {pending && <p role="status">{t("loading")}</p>}
    {detail && <PostingDetail detail={detail} onClose={() => setDetail(null)} />}
  </section>;
}
function PostingDetail({ detail, onClose }) {
  const { t, money } = useEconomyText();
  const rows = [...(detail.data.voucher_postings || []), ...(detail.data.supplierinvoice_postings || []), ...(detail.data.customer_invoices || [])];
  const showSupplier = rows.some((row) => row.supplier_name);
  return <div className="eco-card"><div className="eco-toolbar"><h3>{detail.account.account_number} · {detail.account.account_name}</h3><Button onClick={onClose}>{t("close")}</Button></div><p className="eco-muted">{detail.data.methodology_note}</p><div className="eco-table-scroll" tabIndex={0}><table className="eco-table"><thead><tr><th>{t("month")}</th><th>{t("name")}</th>{showSupplier && <th>{t("supplier")}</th>}<th>{t("total")}</th></tr></thead><tbody>{rows.map((row, index) => <tr key={index}><td>{row.date}</td><td>{row.description}</td>{showSupplier && <td>{row.supplier_name || t("unknownSupplier")}</td>}<td>{money(row.amount)}</td></tr>)}</tbody></table></div></div>;
}
